'use client';

import { PauseWhite, PlayWhite } from 'assets/icons';
import React, { useRef, useState, useEffect, CSSProperties } from 'react';
import { Container } from '..';
import styles from './VideoSectionsWithPhone.module.css';

interface PlayPauseProps {
  isPlaying: boolean;
  onClick: () => void;
}

function PlayPause({ isPlaying, onClick }: PlayPauseProps) {
  return (
    <button
      className={styles.playPauseContainer}
      onClick={onClick}
      aria-label="Play or puase the video"
      aria-pressed={isPlaying ? 'true' : 'false'}
      // onKeyDown={handleKeyDown} // WEB ACCESSIBILITY MAINTENANCE : Added keyboard handler | Need to implement Space/Enter key support for video control
    >
      <div className={styles.playPauseBg}>
        <div className={styles.playPauseIcon}>
          {isPlaying ? (
            <PauseWhite aria-hidden="true" />
          ) : (
            <PlayWhite aria-hidden="true" />
          )}
        </div>
      </div>
    </button>
  );
}

interface PhoneWithVideoProps {
  videoSrc: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  title?: string;
}

function PhoneWithVideo({ videoSrc, videoRef, title }: PhoneWithVideoProps) {
  const Video = (
    <div className={styles.videoOverlay}>
      <video
        ref={videoRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
        }}
        muted
        playsInline
        preload="none"
        aria-label={title || 'Video description for Ultrahuman Features'}
      >
        <source src={videoSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );

  return (
    <div className={styles.phoneSvgContainer}>
      {Video}
      <svg
        width="445"
        height="904"
        viewBox="0 0 445 904"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        role="presentation"
      >
        <mask
          id="mask0_2222_3549"
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="0"
          y="-6"
          width="445"
          height="916"
        >
          <path
            d="M444.652 909.532H0.21875V-5.75317H444.652V909.532ZM86.833 18.5535C64.6099 18.6003 56.4466 20.9654 48.2178 25.3689L47.6787 25.6619C39.6238 30.1014 33.2861 36.5248 28.9375 44.6609C24.4922 52.9778 22.125 61.2277 22.125 83.9773L22.126 820.483C22.1728 842.719 24.5368 850.887 28.9375 859.121L29.2305 859.661C33.6673 867.721 40.0868 874.062 48.2178 878.413C56.5297 882.861 64.7748 885.23 87.5107 885.23H357.36C379.414 885.23 387.833 883.001 395.904 878.808L396.652 878.413C404.964 873.965 411.488 867.438 415.934 859.121C420.379 850.804 422.746 842.554 422.746 819.805V83.9773C422.746 61.9104 420.519 53.4861 416.328 45.4099L415.934 44.6609C411.488 36.344 404.964 29.8168 396.652 25.3689C388.341 20.9211 380.096 18.5525 357.36 18.5525L86.833 18.5535Z"
            fill="black"
          />
        </mask>
        <g mask="url(#mask0_2222_3549)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M441.754 272.473L441.769 271.971C441.884 269.12 442.501 264.509 442.501 264.509H443.717C444.744 264.509 445.573 265.336 445.568 266.367L445.109 360.032C445.103 361.058 444.276 361.89 443.245 361.89L442.184 361.889C442.128 361.863 441.902 361.528 441.756 357.94L441.727 291.361C441.728 281.247 441.736 273.721 441.754 272.473V272.473Z"
            fill="url(#paint0_linear_2222_3549)"
          />
          <mask
            id="mask1_2222_3549"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x="441"
            y="264"
            width="5"
            height="98"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M441.754 272.473L441.769 271.971C441.884 269.12 442.501 264.509 442.501 264.509H443.717C444.744 264.509 445.573 265.336 445.568 266.367L445.109 360.032C445.103 361.058 444.276 361.89 443.245 361.89L442.184 361.889C442.128 361.863 441.902 361.528 441.756 357.94L441.727 291.361C441.728 281.247 441.736 273.721 441.754 272.473V272.473Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask1_2222_3549)"></g>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.93401 308.162C4.81927 305.31 4.20172 300.699 4.20172 300.699H2.98616C1.9589 300.699 1.13261 301.529 1.14068 302.564V359.944C1.14872 360.974 2.42752 361.891 3.45795 361.891L4.5187 361.89C4.57508 361.864 4.80074 361.53 4.94686 357.941L4.97658 319.734C4.97521 314.028 4.96761 309.693 4.94884 308.652L4.93401 308.162Z"
            fill="url(#paint1_linear_2222_3549)"
          />
          <mask
            id="mask2_2222_3549"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x="1"
            y="300"
            width="4"
            height="62"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.93401 308.162C4.81927 305.31 4.20172 300.699 4.20172 300.699H2.98616C1.9589 300.699 1.13261 301.529 1.14068 302.564V359.944C1.14872 360.974 2.42752 361.891 3.45795 361.891L4.5187 361.89C4.57508 361.864 4.80074 361.53 4.94686 357.941L4.97658 319.734C4.97521 314.028 4.96761 309.693 4.94884 308.652L4.93401 308.162Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask2_2222_3549)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M-0.0963022 303.039V300.041L-0.0426984 300.008C0.280969 299.839 1.96212 299.365 2.00631 307.743L2.00719 352.974C2.00719 360.874 1.09392 361.958 0.73542 362.397L-0.3125 360.316C0.964866 358.414 1.21152 356.111 1.22751 352.585L1.22837 311.834C1.22837 307.361 0.270536 303.02 -0.0963022 303.039Z"
              fill="#0C0E14"
            />
          </g>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.93401 227.427C4.81927 224.576 4.20172 219.965 4.20172 219.965H2.98616C1.9589 219.965 1.13261 220.795 1.14068 221.829V279.267C1.14872 280.297 2.42752 281.157 3.45795 281.157L4.5187 281.156C4.57508 281.13 4.80074 280.795 4.94686 277.207L4.97658 239C4.97521 233.294 4.96761 228.958 4.94884 227.918L4.93401 227.427Z"
            fill="url(#paint2_linear_2222_3549)"
          />
          <mask
            id="mask3_2222_3549"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x="1"
            y="219"
            width="4"
            height="63"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.93401 227.427C4.81927 224.576 4.20172 219.965 4.20172 219.965H2.98616C1.9589 219.965 1.13261 220.795 1.14068 221.829V279.267C1.14872 280.297 2.42752 281.157 3.45795 281.157L4.5187 281.156C4.57508 281.13 4.80074 280.795 4.94686 277.207L4.97658 239C4.97521 233.294 4.96761 228.958 4.94884 227.918L4.93401 227.427Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask3_2222_3549)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M-0.0963022 222.309V219.311L-0.0426984 219.278C0.280969 219.109 1.96212 218.635 2.00631 227.013L2.00719 272.244C2.00719 280.144 1.09392 281.228 0.73542 281.667L-0.3125 279.586C0.964866 277.684 1.21152 275.381 1.22751 271.855L1.22837 231.104C1.22837 226.631 0.270536 222.29 -0.0963022 222.309Z"
              fill="#0C0E14"
            />
          </g>
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M4.93401 168.04C4.81927 165.188 4.20172 160.577 4.20172 160.577H2.98616C1.9589 160.577 1.13261 161.407 1.14068 162.442L1.19326 190.096C1.2013 191.126 2.42752 192.076 3.45795 192.076L4.5187 192.075C4.57508 192.049 4.80074 191.714 4.94686 188.126L4.97658 179.612C4.97521 173.906 4.96761 169.571 4.94884 168.53L4.93401 168.04Z"
            fill="url(#paint3_linear_2222_3549)"
          />
          <mask
            id="mask4_2222_3549"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x="1"
            y="160"
            width="4"
            height="33"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M4.93401 168.04C4.81927 165.188 4.20172 160.577 4.20172 160.577H2.98616C1.9589 160.577 1.13261 161.407 1.14068 162.442L1.19326 190.096C1.2013 191.126 2.42752 192.076 3.45795 192.076L4.5187 192.075C4.57508 192.049 4.80074 191.714 4.94686 188.126L4.97658 179.612C4.97521 173.906 4.96761 169.571 4.94884 168.53L4.93401 168.04Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask4_2222_3549)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M0.573675 162.511C0.573675 162.511 0.397505 161.538 0.208719 161.217C-0.116637 160.663 -0.96875 159.887 -0.96875 159.887C-0.639072 159.7 1.50516 159.316 1.54934 167.695L1.55023 183.232C1.55023 191.132 0.167865 192.143 -0.190632 192.583L-0.0978958 191.351C1.00467 189.367 0.814346 186.758 0.830335 183.232V171.972C0.830335 167.499 0.676981 163.014 0.573675 162.511Z"
              fill="#0C0E14"
            />
          </g>
          <path
            d="M3.92187 108.077C3.92187 70.663 3.92187 51.956 11.2031 37.6658C17.6079 25.0957 27.8277 14.8759 40.3977 8.47119C54.688 1.18994 73.395 1.18994 110.809 1.18994L335.677 1.18994C373.091 1.18994 391.798 1.18994 406.088 8.47119C418.659 14.8759 428.878 25.0957 435.283 37.6658C442.564 51.956 442.564 70.663 442.564 108.077V796.241C442.564 833.655 442.564 852.362 435.283 866.652C428.878 879.223 418.659 889.442 406.088 895.847C391.798 903.128 373.091 903.128 335.677 903.128H110.809C73.3949 903.128 54.688 903.128 40.3977 895.847C27.8277 889.442 17.6079 879.223 11.2031 866.652C3.92188 852.362 3.92188 833.655 3.92188 796.241L3.92187 108.077Z"
            fill="#1A1B20"
          />
          <mask
            id="mask5_2222_3549"
            style={{ maskType: 'luminance' }}
            maskUnits="userSpaceOnUse"
            x="3"
            y="1"
            width="440"
            height="903"
          >
            <path
              d="M3.92187 108.077C3.92187 70.663 3.92187 51.956 11.2031 37.6658C17.6079 25.0957 27.8277 14.8759 40.3977 8.47119C54.688 1.18994 73.395 1.18994 110.809 1.18994L335.677 1.18994C373.091 1.18994 391.798 1.18994 406.088 8.47119C418.659 14.8759 428.878 25.0957 435.283 37.6658C442.564 51.956 442.564 70.663 442.564 108.077V796.241C442.564 833.655 442.564 852.362 435.283 866.652C428.878 879.223 418.659 889.442 406.088 895.847C391.798 903.128 373.091 903.128 335.677 903.128H110.809C73.3949 903.128 54.688 903.128 40.3977 895.847C27.8277 889.442 17.6079 879.223 11.2031 866.652C3.92188 852.362 3.92188 833.655 3.92188 796.241L3.92187 108.077Z"
              fill="white"
            />
          </mask>
          <g mask="url(#mask5_2222_3549)">
            <path
              d="M5.77344 109.005C5.77344 71.5908 5.77344 52.8838 13.0547 38.5935C19.4594 26.0235 29.6792 15.8037 42.2493 9.39892C56.5395 2.11768 75.2465 2.11768 112.66 2.11768L332.892 2.11768C370.306 2.11768 389.013 2.11768 403.303 9.39892C415.873 15.8037 426.093 26.0235 432.498 38.5935C439.779 52.8838 439.779 71.5907 439.779 109.005V794.385C439.779 831.799 439.779 850.506 432.498 864.796C426.093 877.366 415.873 887.586 403.303 893.991C389.013 901.272 370.306 901.272 332.892 901.272H112.66C75.2465 901.272 56.5395 901.272 42.2493 893.991C29.6792 887.586 19.4594 877.366 13.0547 864.796C5.77344 850.506 5.77344 831.799 5.77344 794.385V109.005Z"
              fill="url(#paint4_linear_2222_3549)"
            />
            <g filter="url(#filter0_i_2222_3549)">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.625 107.891C7.625 71.5164 7.625 53.3291 14.704 39.4358C20.9308 27.2149 30.8667 17.279 43.0876 11.0521C56.9809 3.97314 75.1683 3.97314 111.543 3.97314L334.003 3.97314C370.378 3.97314 388.565 3.97314 402.459 11.0521C414.679 17.279 424.615 27.2149 430.842 39.4358C437.921 53.3291 437.921 71.5164 437.921 107.891V795.498C437.921 831.873 437.921 850.06 430.842 863.954C424.615 876.174 414.679 886.11 402.459 892.337C388.565 899.416 370.378 899.416 334.003 899.416H111.543C75.1683 899.416 56.9809 899.416 43.0876 892.337C30.8667 886.11 20.9308 876.174 14.704 863.954C7.625 850.06 7.625 831.873 7.625 795.498V107.891Z"
                fill="url(#paint5_linear_2222_3549)"
              />
            </g>
          </g>
        </g>
        <g filter="url(#filter1_i_2222_3549)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M181.047 6.7616L262.655 6.7616L261.858 8.97914C261.725 9.34993 261.374 9.59722 260.98 9.59722H182.581C182.167 9.59722 181.803 9.32469 181.686 8.92766L181.047 6.7616Z"
            fill="url(#paint6_linear_2222_3549)"
          />
        </g>
        <mask
          id="mask6_2222_3549"
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="181"
          y="6"
          width="82"
          height="4"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M181.047 6.7616L262.655 6.7616L261.928 9.16068C261.809 9.55444 261.446 9.82373 261.035 9.82373H182.667C182.256 9.82373 181.893 9.55444 181.773 9.16068L181.047 6.7616Z"
            fill="url(#paint7_linear_2222_3549)"
          />
        </mask>
        <g mask="url(#mask6_2222_3549)">
          <g filter="url(#filter2_i_2222_3549)">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M181.047 5.82581L262.655 5.82581L261.727 8.88794H181.974L181.047 5.82581Z"
              fill="#1B1B1B"
            />
          </g>
        </g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M264.051 29.0309H181.516C172.04 29.0309 164.359 36.7166 164.359 46.1974C164.359 55.6782 172.04 63.3639 181.516 63.3639H264.051C273.526 63.3639 281.207 55.6782 281.207 46.1974C281.207 36.7166 273.526 29.0309 264.051 29.0309Z"
          fill="black"
        />
        <g filter="url(#filter3_di_2222_3549)">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M266.401 50.4581C268.755 49.1525 269.606 46.1846 268.301 43.8292C266.996 41.4737 264.03 40.6227 261.676 41.9283C259.322 43.234 258.471 46.2019 259.776 48.5573C261.081 50.9127 264.047 51.7638 266.401 50.4581Z"
            fill="url(#paint8_linear_2222_3549)"
          />
        </g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M265.621 49.0402C267.191 48.1697 267.758 46.1911 266.888 44.6208C266.018 43.0505 264.041 42.4832 262.471 43.3536C260.902 44.2241 260.335 46.2026 261.205 47.7729C262.075 49.3432 264.052 49.9106 265.621 49.0402Z"
          fill="url(#paint9_linear_2222_3549)"
        />
        <mask
          id="mask7_2222_3549"
          style={{ maskType: 'alpha' }}
          maskUnits="userSpaceOnUse"
          x="260"
          y="42"
          width="8"
          height="8"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M265.621 49.0402C267.191 48.1697 267.758 46.1911 266.888 44.6208C266.018 43.0505 264.041 42.4832 262.471 43.3536C260.902 44.2241 260.335 46.2026 261.205 47.7729C262.075 49.3432 264.052 49.9106 265.621 49.0402Z"
            fill="white"
          />
        </mask>
        <g mask="url(#mask7_2222_3549)"></g>
        <defs>
          <filter
            id="filter0_i_2222_3549"
            x="7.625"
            y="3.97314"
            width="430.297"
            height="896.377"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="0.933684" />
            <feGaussianBlur stdDeviation="2.80105" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.4 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect1_innerShadow_2222_3549"
            />
          </filter>
          <filter
            id="filter1_i_2222_3549"
            x="181.047"
            y="6.7616"
            width="81.6094"
            height="4.70294"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1.86737" />
            <feGaussianBlur stdDeviation="1.40053" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.0514606 0 0 0 0 0.0514606 0 0 0 0 0.0514606 0 0 0 0.5 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect1_innerShadow_2222_3549"
            />
          </filter>
          <filter
            id="filter2_i_2222_3549"
            x="181.047"
            y="5.82581"
            width="81.6094"
            height="4.9295"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="BackgroundImageFix"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1.86737" />
            <feGaussianBlur stdDeviation="1.40053" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.0514606 0 0 0 0 0.0514606 0 0 0 0 0.0514606 0 0 0 0.5 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect1_innerShadow_2222_3549"
            />
          </filter>
          <filter
            id="filter3_di_2222_3549"
            x="259.164"
            y="41.316"
            width="9.75"
            height="10.6881"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.0762948 0"
            />
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow_2222_3549"
            />
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow_2222_3549"
              result="shape"
            />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="0.933684" />
            <feGaussianBlur stdDeviation="0.466842" />
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.197945 0"
            />
            <feBlend
              mode="normal"
              in2="shape"
              result="effect2_innerShadow_2222_3549"
            />
          </filter>
          <linearGradient
            id="paint0_linear_2222_3549"
            x1="441.727"
            y1="266.803"
            x2="437.676"
            y2="361.635"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#292929" />
            <stop offset="0.0907565" stopColor="#444545" />
            <stop offset="0.902379" stopColor="#424344" />
            <stop offset="1" stopColor="#1E1E1E" />
          </linearGradient>
          <linearGradient
            id="paint1_linear_2222_3549"
            x1="1.14062"
            y1="302.141"
            x2="-0.462685"
            y2="361.797"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#292929" />
            <stop offset="0.0907565" stopColor="#444545" />
            <stop offset="0.902379" stopColor="#424344" />
            <stop offset="1" stopColor="#1E1E1E" />
          </linearGradient>
          <linearGradient
            id="paint2_linear_2222_3549"
            x1="1.14062"
            y1="221.406"
            x2="-0.462685"
            y2="281.063"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#292929" />
            <stop offset="0.0907565" stopColor="#444545" />
            <stop offset="0.902379" stopColor="#424344" />
            <stop offset="1" stopColor="#1E1E1E" />
          </linearGradient>
          <linearGradient
            id="paint3_linear_2222_3549"
            x1="1.14062"
            y1="161.319"
            x2="0.715567"
            y2="192.044"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#292929" />
            <stop offset="0.0907565" stopColor="#444545" />
            <stop offset="0.902379" stopColor="#424344" />
            <stop offset="1" stopColor="#1E1E1E" />
          </linearGradient>
          <linearGradient
            id="paint4_linear_2222_3549"
            x1="222.776"
            y1="2.11768"
            x2="222.776"
            y2="901.272"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="white" />
            <stop offset="0.0360569" />
            <stop offset="0.281688" stopColor="#373737" />
          </linearGradient>
          <linearGradient
            id="paint5_linear_2222_3549"
            x1="222.773"
            y1="3.97314"
            x2="222.773"
            y2="899.416"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#0D0D0D" />
            <stop offset="1" stopColor="#0A0A0A" />
          </linearGradient>
          <linearGradient
            id="paint6_linear_2222_3549"
            x1="181.34"
            y1="10.5309"
            x2="263.037"
            y2="9.59722"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#161616" />
            <stop offset="0.0506597" stopColor="#383838" />
            <stop offset="0.972459" stopColor="#343434" />
            <stop offset="1" stopColor="#161616" />
          </linearGradient>
          <linearGradient
            id="paint7_linear_2222_3549"
            x1="181.34"
            y1="10.5309"
            x2="263.037"
            y2="9.59722"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#161616" />
            <stop offset="0.0506597" stopColor="#383838" />
            <stop offset="0.972459" stopColor="#343434" />
            <stop offset="1" stopColor="#161616" />
          </linearGradient>
          <linearGradient
            id="paint8_linear_2222_3549"
            x1="259.164"
            y1="41.316"
            x2="259.164"
            y2="51.0704"
            gradientUnits="userSpaceOnUse"
          >
            <stop />
            <stop offset="1" stopColor="#2F2F2F" />
          </linearGradient>
          <linearGradient
            id="paint9_linear_2222_3549"
            x1="260.797"
            y1="42.9454"
            x2="260.797"
            y2="49.4483"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#313131" />
            <stop offset="1" stopColor="#151515" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

type SectionData = {
  id: string;
  title: string;
  subtitle: string;
  ctaText?: string;
  videoSrc: string;
  backgroundColor: string;
} & (
  | { ctaHref: string; onClick?: never }
  | { ctaHref?: never; onClick: () => void }
);

interface VideoSectionProps {
  section: SectionData;
  isReversed: boolean;
}

function VideoSection({ section, isReversed }: VideoSectionProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        // If video has ended, reset to beginning for manual replay
        if (hasPlayedOnce) {
          videoRef.current.currentTime = 0;
        }
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        const isIntersecting = entry.isIntersecting;
        setIsVisible(isIntersecting);

        if (videoRef.current) {
          if (isIntersecting) {
            // Load video only when it becomes visible
            if (!hasLoaded) {
              videoRef.current.load();
              setHasLoaded(true);
            }

            // Only autoplay if the video hasn't played once yet
            if (!hasPlayedOnce) {
              const playPromise = videoRef.current.play();
              if (playPromise !== undefined) {
                playPromise
                  .then(() => {
                    setIsPlaying(true);
                  })
                  .catch(() => {
                    // Handle play failure silently
                    setIsPlaying(false);
                  });
              }
            }
          } else {
            videoRef.current.pause();
            setIsPlaying(false);
          }
        }
      },
      {
        threshold: 0.3, // Reduced threshold for earlier loading
        rootMargin: '100px', // Load when approaching viewport
      },
    );

    // Handle video ending - pause at final frame
    const handleVideoEnded = () => {
      setIsPlaying(false);
      setHasPlayedOnce(true);
    };

    const currentSection = sectionRef.current;
    const currentVideo = videoRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    if (currentVideo) {
      currentVideo.addEventListener('ended', handleVideoEnded);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
      observer.disconnect();
      if (currentVideo) {
        currentVideo.removeEventListener('ended', handleVideoEnded);
      }
    };
  }, [hasLoaded, isVisible, hasPlayedOnce]);

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      data-reversed={isReversed ? 'true' : 'false'}
      style={{ '--bg-color': section.backgroundColor } as CSSProperties}
    >
      <div className={styles.bgGradient} />
      <Container className={styles.container}>
        <div className={styles.content}>
          <h2
            className={styles.title}
            id={section.title.replaceAll(' ', '').toLocaleLowerCase()} // WEB ACCESSIBILITY MAINTENANCE : Added id | Should have unique ID for ARIA associations
          >
            <span className={styles.highlight}>{section.title}</span>{' '}
            <span className={styles.subtitle}>{section.subtitle}</span>
          </h2>
          {section.ctaText ? (
            <>
              {'ctaHref' in section ? (
                <a
                  href={section.ctaHref}
                  className={styles.cta}
                  aria-label={section.ctaText}
                >
                  {section.ctaText}
                </a>
              ) : (
                <button
                  onClick={section.onClick}
                  className={styles.cta}
                  aria-label={section.ctaText}
                  type="button"
                >
                  {section.ctaText}
                </button>
              )}
            </>
          ) : null}
        </div>
        <div
          className={styles.phoneContainer}
          aria-labelledby={section.title}
          role="region"
        >
          <PhoneWithVideo
            videoSrc={section.videoSrc}
            videoRef={videoRef}
            title={section.title}
          />
        </div>
      </Container>
      <PlayPause isPlaying={isPlaying} onClick={handlePlayPause} />
    </section>
  );
}

interface VideoSectionsWithPhoneProps {
  className?: string;
  sections: SectionData[];
}

export const VideoSectionsWithPhone = ({
  className,
  sections,
}: VideoSectionsWithPhoneProps) => (
  <div
    className={
      className
        ? `${styles.videoSectionsContainer} ${className}`
        : styles.videoSectionsContainer
    }
  >
    {sections.map((section, index) => (
      <VideoSection
        key={section.id}
        section={section}
        isReversed={index % 2 === 0}
      />
    ))}
  </div>
);

/*
WEB ACCESSIBILITY MAINTENANCE : STRUCTURAL CHANGES NEEDED

1. HEADING HIERARCHY ✅
   - Current: Uses h2 for section titles
   - Required: Ensure proper heading hierarchy in page context
   - Action: Verify h2 is appropriate or adjust based on parent page structure

2. VIDEO ACCESSIBILITY
   - Current: Videos autoplay and lack comprehensive accessibility features
   - Required: Full video accessibility support
   - Action: 
     * Add captions files for all video content
     * Implement proper ARIA announcements for video state changes
     * Consider adding video description tracks for visual content
     * Add option to disable autoplay for users with motion sensitivities

3. KEYBOARD NAVIGATION
   - Current: PlayPause button missing keyboard event handlers
   - Required: Full keyboard support for all interactive elements
   - Action: 
     * Implement handleKeyDown function for video controls
     * Ensure all CTA buttons/links are keyboard accessible
     * Add proper focus management between sections

4. MOTION ACCESSIBILITY
   - Current: Complex animations and autoplay videos
   - Required: Respect user's motion preferences
   - Action: 
     * Check for prefers-reduced-motion media query
     * Disable autoplay and reduce animations for motion-sensitive users
     * Provide alternative static content when needed

5. UNIQUE IDENTIFIERS ✅
   - Current: Empty id attributes and ARIA associations
   - Required: Unique IDs for proper ARIA relationships
   - Action: 
     * Generate unique IDs for each section based on section.id
     * Connect headings with video regions using aria-labelledby
     * Ensure all ARIA associations are properly linked

6. CTA LINK ACCESSIBILITY ✅
   - Current: Generic link/button text
   - Required: Descriptive link purposes
   - Action: 
     * Provide more context in aria-label attributes
     * Consider adding visually hidden text for better context
     * Ensure link destinations are clearly communicated

7. COLOR CONTRAST
   - Current: Complex gradient backgrounds with text overlay
   - Required: WCAG 2.2 contrast compliance
   - Action: 
     * Verify all text meets 4.5:1 contrast ratio against backgrounds
     * Test contrast with gradient variations
     * Ensure subtitle text (rgba(255, 255, 255, 0.4)) meets requirements

8. FOCUS MANAGEMENT
   - Current: No visible focus indicators specified
   - Required: Clear focus states for all interactive elements
   - Action: 
     * Add focus styles for CTA buttons/links
     * Ensure focus is visible against gradient backgrounds
     * Implement focus trapping if needed for video interactions
*/

/*
WEB ACCESSIBILITY MAINTENANCE : TESTING CHECKLIST

MANUAL TESTING REQUIRED:
□ Keyboard navigation through all sections and controls
□ Screen reader testing for video content and descriptions
□ Video controls accessible via keyboard (Space/Enter/Arrow keys)
□ CTA buttons/links clearly announced with context
□ 200-400% zoom testing for content reflow
□ High contrast mode testing
□ Motion sensitivity testing (prefers-reduced-motion)
□ Video captions display correctly when implemented

AUTOMATED TESTING:
□ Run eslint-plugin-jsx-a11y checks
□ Lighthouse accessibility audit
□ axe-core accessibility testing
□ Color contrast validation for all text elements
□ Heading structure validation

FOCUS TESTING:
□ Focus visible on all CTA buttons/links
□ Focus visible on PlayPause buttons
□ Focus moves logically through sections
□ Focus indicators visible against gradient backgrounds
□ No focus traps or lost focus issues

VIDEO TESTING:
□ Video controls announce state changes
□ Videos can be paused via keyboard
□ Videos respect user's autoplay preferences
□ Captions are available and functional (when implemented)
□ Video descriptions available for visual content

INTERACTION TESTING:
□ All sections navigable via keyboard
□ CTA actions clearly communicated
□ Video playback controllable by users
□ No content relies solely on visual cues
*/
